import { Guid } from "guid-typescript";
import {
  CommonHelperFunctions,
  ICodeGenerationContext,
  IFillStyle,
  IKuikaStyle,
  IStylingPosition,
  IStylingTypography,
  SourceCodeGenerateTypes
} from "../../index";
import { IFilterItem } from "../models/effects-filter-item";
import { ITransformItem } from "../models/effects-transform-item";
import { CubicBezierTuple, ITransitionItem, TransitionTimingFunction } from "../models/effects-transition-item";
import { AlignItemsTypes, DisplayTypes, TextAlignTypes } from "../models/position-style";
import { IStylingBorder } from "../models/styling-border";
import { IStylingEffects } from "../models/styling-effects";
import { IStylingShadow } from "../models/styling-shadow";

export type NumberStyle = "%" | "px" | "pt";

/**
 * CSS gradient function prefixes Kuika recognises. A fill value that begins with
 * one of these is a gradient and must be emitted as `background-image`, not
 * `background-color` (the latter is invalid CSS for gradients).
 */
export const GRADIENT_PREFIXES: readonly string[] = ["linear-gradient", "radial-gradient", "conic-gradient"];

/**
 * True when `value` is any CSS gradient (linear / radial / conic). Single source
 * of truth for gradient detection across the designer, kuika-cl-model and the
 * generated React app (client_code_generator bundles this module).
 */
export const isGradient = (value?: string | null): boolean => {
  if (!value) {
    return false;
  }
  const trimmed: string = value.trim();
  return GRADIENT_PREFIXES.some((prefix: string): boolean => trimmed.startsWith(prefix));
};

/** CSS-wide keywords: legal only as a whole value, never inside a per-layer list. */
const CSS_WIDE_KEYWORDS: readonly string[] = ["inherit", "initial", "unset", "revert", "revert-layer"];

/** The background keys a fill / gradient-text emission can contribute. */
interface IBackgroundLayerStyles {
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundRepeat?: string;
  backgroundSize?: string;
  backgroundPosition?: string;
  backgroundClip?: string;
  WebkitBackgroundClip?: string;
  WebkitTextFillColor?: string;
}

/**
 * Flat CSS strings derived from an effects payload, for the designer's preset
 * preview tiles (and any other "show me this effect" surface). A typed,
 * public projection of the same transform / filter / transition / outline
 * builders the canvas uses — so previews stay byte-identical with the real
 * generated effect without duplicating the builders. Looping `animations[]`
 * and `entryAnimation` are intentionally NOT projected here: those are
 * `@keyframes`, which a preview must drive imperatively (e.g. WAAPI).
 */
export interface IEffectsPreviewStyle {
  opacity?: number;
  transform?: string;
  transition?: string;
  filter?: string;
  outline?: string;
  outlineOffset?: string;
}

/**
 * Result of {@link StyleHelper.parseTransformCss}. The per-item transform chain
 * plus an optional self-`perspective(...)` value lifted out to the effects
 * `perspective` scalar — the canvas/codegen builder emits perspective as a
 * leading `perspective(Npx)` function, so on the reverse parse it must be
 * separated back out of the function list.
 */
export interface IParsedTransformCss {
  transforms: Omit<ITransformItem, "id">[];
  perspective?: number;
}

/** One `name(args)` segment extracted from a CSS function-list value. */
interface ICssFunctionToken {
  name: string;
  args: string;
}

export class StyleHelper {
  public static convertToHtmlStyleFromStyleObj = (
    context: ICodeGenerationContext,
    styleObj: IKuikaStyle,
    kuikaid: string,
    generateType: SourceCodeGenerateTypes
  ) => {
    const borderStyles = StyleHelper.getBorderStyles(context, styleObj, kuikaid);
    const shadowStyles = StyleHelper.getShadowStyles(context, styleObj, kuikaid);
    const fillStyles = StyleHelper.getFillStyles(context, styleObj, generateType, kuikaid);
    const positionStyles = StyleHelper.getPositionStyles(context, styleObj, kuikaid);
    const textStyles = StyleHelper.getTextStyles(context, styleObj, kuikaid);
    const effectsStyles = StyleHelper.getEffectsStyles(context, styleObj, kuikaid);

    // KOA-18170: gradient text and a Fill share this element's background. Spread
    // last, so the layered version wins over both sections' single-layer keys.
    const gradientTextOverFill: IBackgroundLayerStyles | undefined = StyleHelper.composeGradientTextOverFill(
      fillStyles,
      textStyles
    );

    return {
      ...borderStyles,
      ...shadowStyles,
      ...fillStyles,
      ...positionStyles,
      ...textStyles,
      ...effectsStyles,
      ...gradientTextOverFill
    };
  };

  /**
   * `background-clip: text` (gradient text, see {@link StyleHelper.getTextStyle})
   * clips the element's WHOLE background to its glyphs, wiping a coexisting Fill.
   * Fix: two layers — the text gradient clipped to `text`, the fill to `border-box`.
   * Returns undefined when the two don't collide, keeping the old single layer.
   */
  private static composeGradientTextOverFill = (
    fillStyles?: IBackgroundLayerStyles,
    textStyles?: IBackgroundLayerStyles
  ): IBackgroundLayerStyles | undefined => {
    const textGradient: string | undefined = textStyles?.backgroundImage;
    const isGradientText: boolean = textStyles?.backgroundClip === "text" && isGradient(textGradient);
    if (!isGradientText || !fillStyles) {
      return undefined;
    }

    const fillImage: string | undefined = fillStyles.backgroundImage;
    const hasFillBackground: boolean = Boolean(fillImage) || Boolean(fillStyles.backgroundColor);
    if (!hasFillBackground) {
      return undefined;
    }

    // `background-color` follows the LAST layer's clip, so a solid fill still needs
    // the `none` layer to keep the list two long.
    const composed: IBackgroundLayerStyles = {
      backgroundImage: `${textGradient}, ${fillImage || "none"}`,
      WebkitBackgroundClip: "text, border-box",
      backgroundClip: "text, border-box"
    };

    // repeat / size / position belong to the fill layer only, so each is paired
    // with the initial value for the gradient layer.
    if (fillStyles.backgroundRepeat) {
      composed.backgroundRepeat = `no-repeat, ${StyleHelper.toBackgroundLayerValue(fillStyles.backgroundRepeat, "repeat")}`;
    }
    if (fillStyles.backgroundSize) {
      composed.backgroundSize = `auto, ${StyleHelper.toBackgroundLayerValue(fillStyles.backgroundSize, "auto")}`;
    }
    if (fillStyles.backgroundPosition) {
      composed.backgroundPosition = `0% 0%, ${StyleHelper.toBackgroundLayerValue(fillStyles.backgroundPosition, "0% 0%")}`;
    }

    return composed;
  };

  /** Makes one background value safe to use inside a per-layer list. */
  private static toBackgroundLayerValue = (value: string, initial: string): string =>
    CSS_WIDE_KEYWORDS.includes(value.trim().toLowerCase()) ? initial : value;

  public static setEmptyFieldsInStyleObj = (styleObj?: IKuikaStyle) => {
    if (styleObj === undefined) {
      styleObj = {} as IKuikaStyle;
    }
    if (styleObj.border === undefined) {
      styleObj.border = {
        borderId: null,
        isVisible: false,
        isBorderWidthLocked: true,
        isBorderRadiusLocked: true,
        borderStyle: "solid"
      } as IStylingBorder;
    }

    if (styleObj.shadow === undefined) {
      styleObj.shadow = { shadowId: null, isVisible: false } as IStylingShadow;
    }

    if (styleObj.fill === undefined) {
      styleObj.fill = {
        fillId: null,
        isVisible: false,
        backgroundImage: undefined,
        repeatSwitch: false,
        bgImageSize: false,
        stretch: false,
        imageFit: undefined,
        bgImagePosition: "left"
      } as IFillStyle;
    }

    if (styleObj.position === undefined) {
      styleObj.position = {
        positionId: null,
        widthStyle: "px",
        heightStyle: "px",
        maxWidthStyle: "px",
        maxHeightStyle: "px",
        minWidthStyle: "px",
        minHeightStyle: "px",
        // isMarginLocked: true,
        // isPaddingLocked: true,
        alignItems: undefined,
        textAlign: undefined,
        display: undefined
      } as IStylingPosition;
    }

    if (styleObj.text === undefined) {
      styleObj.text = {
        typoId: null,
        lineHeightStyle: "",
        letterSpacingStyle: "",
        textDecoration: "",
        // textTransform: undefined,
        fontSizeStyle: "px"
      } as IStylingTypography;
    }
    return styleObj;
  };

  /* Border */

  private static getBorderStyles = (context: ICodeGenerationContext, styleObj: IKuikaStyle, kuikaid: string): any => {
    const borderDef: IStylingBorder | undefined = StyleHelper.convertIStylingBorderToIFormStylingBorder(
      context,
      kuikaid,
      styleObj?.border
    );
    if (!borderDef) {
      return;
    }
    let returnObj = {} as any;
    if (borderDef.borderIds && borderDef.borderIds.length > 0) {
      for (const borderId of borderDef.borderIds) {
        const borderIdLower = borderId?.toLowerCase();
        const formStylingBorder = context.project.stylingBorder.find(
          (x) => x.borderId?.toLowerCase() === borderIdLower
        );
        if (formStylingBorder) {
          returnObj = this.mergeStyle(returnObj, formStylingBorder);
        }
      }
    }
    returnObj = this.mergeStyle(returnObj, borderDef);
    returnObj = StyleHelper.getBorderStyle(returnObj);
    return returnObj;
  };

  private static getBorderStyle = (typoyDef: IStylingBorder): any => {
    const returnObj = {} as any;

    if (typoyDef.borderTopLeftRadius || typoyDef.borderTopLeftRadius == 0) {
      returnObj.borderTopLeftRadius = typoyDef.borderTopLeftRadius;
    }
    if (typoyDef.borderTopRightRadius || typoyDef.borderTopRightRadius == 0) {
      returnObj.borderTopRightRadius = typoyDef.borderTopRightRadius;
    }

    if (typoyDef.borderBottomRightRadius || typoyDef.borderBottomRightRadius == 0) {
      returnObj.borderBottomRightRadius = typoyDef.borderBottomRightRadius;
    }
    if (typoyDef.borderBottomLeftRadius || typoyDef.borderBottomLeftRadius == 0) {
      returnObj.borderBottomLeftRadius = typoyDef.borderBottomLeftRadius;
    }

    if (typoyDef.borderTopWidth) returnObj.borderTopWidth = typoyDef.borderTopWidth;
    if (typoyDef.borderRightWidth) returnObj.borderRightWidth = typoyDef.borderRightWidth;
    if (typoyDef.borderBottomWidth) returnObj.borderBottomWidth = typoyDef.borderBottomWidth;
    if (typoyDef.borderLeftWidth) returnObj.borderLeftWidth = typoyDef.borderLeftWidth;

    returnObj.borderColor = typoyDef.borderColor;
    returnObj.borderStyle = typoyDef.borderStyle;

    if ((returnObj.borderColor || returnObj.borderStyle) && !returnObj.borderWidth) {
      if (!returnObj.borderTopWidth) returnObj.borderTopWidth = 0;
      if (!returnObj.borderRightWidth) returnObj.borderRightWidth = 0;
      if (!returnObj.borderBottomWidth) returnObj.borderBottomWidth = 0;
      if (!returnObj.borderLeftWidth) returnObj.borderLeftWidth = 0;
    }

    return returnObj;
  };

  static convertIStylingBorderToIFormStylingBorder = (
    context: ICodeGenerationContext,
    kuikaid: string,
    kuikaStyle?: IStylingBorder
  ): IStylingBorder | undefined => {
    if (kuikaStyle && kuikaStyle.borderId && kuikaStyle.borderId.length > 0) {
      const borderIdList = kuikaStyle.borderId.split("|");
      if (borderIdList !== undefined) {
        kuikaStyle.borderIds = [];
        borderIdList.forEach((borderId) => {
          if (borderId != null && borderId.length > 0) {
            const borderIdLower = borderId?.toLowerCase();
            if (context.project.stylingBorder?.find((x) => x.borderId?.toLowerCase() === borderIdLower)) {
              kuikaStyle.borderIds?.push(borderIdLower);
            }
          }
        });
      }
      kuikaStyle.borderId = null;
    }
    if (
      kuikaStyle &&
      context.projectTempStyles.tempStylingBorder &&
      kuikaid === context.projectTempStyles.tempStylingBorder.kuikaid
    ) {
      kuikaStyle.name = context.projectTempStyles.tempStylingBorder.name;
      kuikaStyle.isVisible = context.projectTempStyles.tempStylingBorder.isVisible;
      kuikaStyle.borderColor = context.projectTempStyles.tempStylingBorder.borderColor;
      kuikaStyle.isBorderWidthLocked = context.projectTempStyles.tempStylingBorder.isBorderWidthLocked;
      kuikaStyle.borderTopWidth = context.projectTempStyles.tempStylingBorder.borderTopWidth;
      kuikaStyle.borderRightWidth = context.projectTempStyles.tempStylingBorder.borderRightWidth;
      kuikaStyle.borderBottomWidth = context.projectTempStyles.tempStylingBorder.borderBottomWidth;
      kuikaStyle.borderLeftWidth = context.projectTempStyles.tempStylingBorder.borderLeftWidth;
      kuikaStyle.isBorderRadiusLocked = context.projectTempStyles.tempStylingBorder.isBorderRadiusLocked;
      kuikaStyle.borderTopLeftRadius = context.projectTempStyles.tempStylingBorder.borderTopLeftRadius;
      kuikaStyle.borderTopRightRadius = context.projectTempStyles.tempStylingBorder.borderTopRightRadius;
      kuikaStyle.borderBottomRightRadius = context.projectTempStyles.tempStylingBorder.borderBottomRightRadius;
      kuikaStyle.borderBottomLeftRadius = context.projectTempStyles.tempStylingBorder.borderBottomLeftRadius;
      kuikaStyle.borderStyle = context.projectTempStyles.tempStylingBorder.borderStyle;
    }
    return kuikaStyle;
  };

  static convertIFormStylingBorderToIStylingBorder = (
    kuikaid: string,
    kuikaStyleForm?: IStylingBorder,
    customStylingBorderBackup?: IStylingBorder
  ): IStylingBorder | undefined => {
    if (kuikaStyleForm) {
      kuikaStyleForm.borderId = null;
    }
    if (kuikaStyleForm && kuikaStyleForm.borderIds && kuikaStyleForm.borderIds.length > 0) {
      // Use filter and join for efficient string building
      const validIds = kuikaStyleForm.borderIds.filter((id) => id != null && id.length > 0);
      kuikaStyleForm.borderId = validIds.length > 0 ? validIds.join("|") : null;
    }
    if (
      customStylingBorderBackup &&
      kuikaid &&
      kuikaid.length > 0 &&
      kuikaStyleForm &&
      customStylingBorderBackup?.kuikaid === kuikaid
    ) {
      kuikaStyleForm.name = customStylingBorderBackup.name;
      kuikaStyleForm.isVisible = customStylingBorderBackup.isVisible;
      kuikaStyleForm.borderColor = customStylingBorderBackup.borderColor;
      kuikaStyleForm.isBorderWidthLocked = customStylingBorderBackup.isBorderWidthLocked;
      kuikaStyleForm.borderTopWidth = customStylingBorderBackup.borderTopWidth;
      kuikaStyleForm.borderRightWidth = customStylingBorderBackup.borderRightWidth;
      kuikaStyleForm.borderBottomWidth = customStylingBorderBackup.borderBottomWidth;
      kuikaStyleForm.borderLeftWidth = customStylingBorderBackup.borderLeftWidth;
      kuikaStyleForm.isBorderRadiusLocked = customStylingBorderBackup.isBorderRadiusLocked;
      kuikaStyleForm.borderTopLeftRadius = customStylingBorderBackup.borderTopLeftRadius;
      kuikaStyleForm.borderTopRightRadius = customStylingBorderBackup.borderTopRightRadius;
      kuikaStyleForm.borderBottomRightRadius = customStylingBorderBackup.borderBottomRightRadius;
      kuikaStyleForm.borderBottomLeftRadius = customStylingBorderBackup.borderBottomLeftRadius;
      kuikaStyleForm.borderStyle = customStylingBorderBackup.borderStyle;
    }
    return kuikaStyleForm;
  };

  /* Shadow */

  private static getShadowStyles = (context: ICodeGenerationContext, styleObj: IKuikaStyle, kuikaid: string): any => {
    const shadowDef: IStylingShadow | undefined = StyleHelper.convertIStylingShadowToIFormStylingShadow(
      context,
      kuikaid,
      styleObj?.shadow
    );
    if (!shadowDef) {
      return;
    }
    let returnObj = {} as any;
    if (shadowDef.shadowIds && shadowDef.shadowIds.length > 0) {
      for (const shadowId of shadowDef.shadowIds) {
        const shadowIdLower = shadowId?.toLowerCase();
        const formStylingShadow = context.project.stylingShadow.find(
          (x) => x.shadowId?.toLowerCase() === shadowIdLower
        );
        if (formStylingShadow) {
          returnObj = this.mergeStyle(returnObj, formStylingShadow);
        }
      }
    }
    returnObj = this.mergeStyle(returnObj, shadowDef);
    returnObj = StyleHelper.getShadowStyle(returnObj);
    return returnObj;
  };

  private static getShadowStyle = (typoyDef: IStylingShadow): any => {
    const returnObj = {} as any;

    let { color } = typoyDef;
    let { x } = typoyDef;
    let { y } = typoyDef;
    let { b } = typoyDef;
    let { s } = typoyDef;
    const { isInset } = typoyDef;

    color = color === undefined ? "#000" : color;
    x = x === undefined ? 0 : x;
    y = y === undefined ? 0 : y;
    b = b === undefined ? 0 : b;
    s = s === undefined ? 0 : s;

    if (b === 0 && s === 0) return returnObj;

    const shadowCss = `${isInset ? "inset " : ""}${x}px ${y}px ${b}px ${s}px ${color}`;

    returnObj.boxShadow = shadowCss;

    return returnObj;
  };

  static convertIStylingShadowToIFormStylingShadow = (
    context: ICodeGenerationContext,
    kuikaid: string,
    kuikaStyle?: IStylingShadow
  ): IStylingShadow | undefined => {
    if (kuikaStyle && kuikaStyle.shadowId && kuikaStyle.shadowId.length > 0) {
      const shadowIdList = kuikaStyle.shadowId.split("|");
      if (shadowIdList !== undefined) {
        kuikaStyle.shadowIds = [];
        shadowIdList.forEach((shadowId) => {
          if (shadowId != null && shadowId.length > 0) {
            const shadowIdLower = shadowId?.toLowerCase();
            if (context.project.stylingShadow?.find((x) => x.shadowId?.toLowerCase() === shadowIdLower)) {
              kuikaStyle.shadowIds?.push(shadowIdLower);
            }
          }
        });
      }
      kuikaStyle.shadowId = null;
    }
    if (
      kuikaStyle &&
      context.projectTempStyles.tempStylingShadow &&
      kuikaid === context.projectTempStyles.tempStylingShadow.kuikaid
    ) {
      kuikaStyle.name = context.projectTempStyles.tempStylingShadow.name;
      kuikaStyle.isVisible = context.projectTempStyles.tempStylingShadow.isVisible;
      kuikaStyle.color = context.projectTempStyles.tempStylingShadow.color;
      kuikaStyle.x = context.projectTempStyles.tempStylingShadow.x;
      kuikaStyle.y = context.projectTempStyles.tempStylingShadow.y;
      kuikaStyle.b = context.projectTempStyles.tempStylingShadow.b;
      kuikaStyle.s = context.projectTempStyles.tempStylingShadow.s;
      kuikaStyle.isInset = context.projectTempStyles.tempStylingShadow.isInset;
    }
    return kuikaStyle;
  };

  static convertIFormStylingShadowToIStylingShadow = (
    kuikaid: string,
    kuikaStyleForm?: IStylingShadow,
    customStylingShadowBackup?: IStylingShadow
  ): IStylingShadow | undefined => {
    if (kuikaStyleForm) {
      kuikaStyleForm.shadowId = null;
    }
    if (kuikaStyleForm && kuikaStyleForm.shadowIds && kuikaStyleForm.shadowIds.length > 0) {
      // Use filter and join for efficient string building
      const validIds = kuikaStyleForm.shadowIds.filter((id) => id != null && id.length > 0);
      kuikaStyleForm.shadowId = validIds.length > 0 ? validIds.join("|") : null;
    }
    if (
      customStylingShadowBackup &&
      kuikaid &&
      kuikaid.length > 0 &&
      kuikaStyleForm &&
      customStylingShadowBackup?.kuikaid === kuikaid
    ) {
      kuikaStyleForm.name = customStylingShadowBackup.name;
      kuikaStyleForm.isVisible = customStylingShadowBackup.isVisible;
      kuikaStyleForm.color = customStylingShadowBackup.color;
      kuikaStyleForm.x = customStylingShadowBackup.x;
      kuikaStyleForm.y = customStylingShadowBackup.y;
      kuikaStyleForm.b = customStylingShadowBackup.b;
      kuikaStyleForm.color = customStylingShadowBackup.color;
      kuikaStyleForm.s = customStylingShadowBackup.s;
      kuikaStyleForm.isInset = customStylingShadowBackup.isInset;
    }
    return kuikaStyleForm;
  };

  /* Effects */

  private static getEffectsStyles = (context: ICodeGenerationContext, styleObj: IKuikaStyle, kuikaid: string): any => {
    const effectsDef: IStylingEffects | undefined = StyleHelper.convertIStylingEffectsToIFormStylingEffects(
      context,
      kuikaid,
      styleObj?.effects
    );
    if (!effectsDef) {
      return;
    }
    let returnObj = {} as any;
    if (effectsDef.effectsIds && effectsDef.effectsIds.length > 0) {
      for (const effectsId of effectsDef.effectsIds) {
        const effectsIdLower = effectsId?.toLowerCase();
        const formStylingEffects = context.project.stylingEffects?.find(
          (x) => x.effectsId?.toLowerCase() === effectsIdLower
        );
        if (formStylingEffects) {
          returnObj = this.mergeStyle(returnObj, formStylingEffects);
        }
      }
    }
    returnObj = this.mergeStyle(returnObj, effectsDef);
    returnObj = StyleHelper.getEffectsStyle(returnObj);
    return returnObj;
  };

  private static getEffectsStyle = (effectsDef: IStylingEffects): any => {
    const returnObj = {} as any;
    if (effectsDef.opacity !== undefined && effectsDef.opacity !== null) {
      returnObj.opacity = effectsDef.opacity;
    }
    if (effectsDef.cursor) {
      returnObj.cursor = effectsDef.cursor;
    }
    if (effectsDef.pointerEvents) {
      returnObj.pointerEvents = effectsDef.pointerEvents;
    }
    if (effectsDef.mixBlendMode) {
      returnObj.mixBlendMode = effectsDef.mixBlendMode;
    }
    if (effectsDef.outlineStyle) {
      returnObj.outlineStyle = effectsDef.outlineStyle;
      if (effectsDef.outlineStyle !== "none") {
        if (effectsDef.outlineWidth !== undefined && effectsDef.outlineWidth !== null) {
          returnObj.outlineWidth = effectsDef.outlineWidth;
        }
        if (effectsDef.outlineColor) {
          returnObj.outlineColor = effectsDef.outlineColor;
        }
        if (effectsDef.outlineOffset !== undefined && effectsDef.outlineOffset !== null) {
          returnObj.outlineOffset = effectsDef.outlineOffset;
        }
      }
    }
    // Faz 3: Multi-item arrays + Transform Settings → React inline-style strings.
    //
    // The canvas (and any generated React component using this output as the
    // `style` prop) cannot consume raw arrays — React inline style expects
    // `transform: "translate(20px,0) scale(1.2)"`, NOT `transforms: [...]`.
    // Self perspective is folded into the same `transform` declaration so the
    // CSS cascade can't drop one of two `transform:` rules.
    const transformString = StyleHelper.buildTransformInlineStyle(effectsDef.transforms, effectsDef.perspective);
    if (transformString) {
      returnObj.transform = transformString;
    }
    const transitionString = StyleHelper.buildTransitionInlineStyle(effectsDef.transitions);
    if (transitionString) {
      returnObj.transition = transitionString;
    }
    // transform-origin: percent shorthand. Defaults to 50% / 50% when only one
    // axis is set so the emitted CSS is well-formed.
    if (
      (effectsDef.transformOriginX !== undefined && effectsDef.transformOriginX !== null) ||
      (effectsDef.transformOriginY !== undefined && effectsDef.transformOriginY !== null)
    ) {
      const ox = effectsDef.transformOriginX ?? 50;
      const oy = effectsDef.transformOriginY ?? 50;
      returnObj.transformOrigin = `${ox}% ${oy}%`;
    }
    // Children perspective is the CSS `perspective` property (applied to the
    // PARENT to give its children a 3D context). Self perspective lives inside
    // the `transform` declaration via perspective(...) function (handled above).
    if (
      effectsDef.childrenPerspective !== undefined &&
      effectsDef.childrenPerspective !== null &&
      effectsDef.childrenPerspective > 0
    ) {
      returnObj.perspective = `${effectsDef.childrenPerspective}px`;
    }
    if (
      (effectsDef.childrenPerspectiveOriginX !== undefined && effectsDef.childrenPerspectiveOriginX !== null) ||
      (effectsDef.childrenPerspectiveOriginY !== undefined && effectsDef.childrenPerspectiveOriginY !== null)
    ) {
      const cx = effectsDef.childrenPerspectiveOriginX ?? 50;
      const cy = effectsDef.childrenPerspectiveOriginY ?? 50;
      returnObj.perspectiveOrigin = `${cx}% ${cy}%`;
    }
    if (effectsDef.backfaceVisibility) {
      returnObj.backfaceVisibility = effectsDef.backfaceVisibility;
    }
    if (effectsDef.transformStyle) {
      returnObj.transformStyle = effectsDef.transformStyle;
    }
    // Faz 4: Filter chain → React inline `filter` string. Array order ≡ CSS
    // function order (CSS spec — `grayscale(100%) blur(4px)` ≠ `blur(4px)
    // grayscale(100%)`). Mirror of the Backend EffectsCssBuilder AppendFilter
    // logic — keep the two in sync when editing either side.
    const filterString = StyleHelper.buildFilterInlineStyle(effectsDef.filters);
    if (filterString) {
      returnObj.filter = filterString;
    }
    // Backdrop filter emits both vendor-prefixed (Safari ≤ 13) and unprefixed
    // declarations. React inline-style camelCase keys: `WebkitBackdropFilter`
    // for the vendor prefix, `backdropFilter` for the standard.
    const backdropFilterString = StyleHelper.buildFilterInlineStyle(effectsDef.backdropFilters);
    if (backdropFilterString) {
      returnObj.WebkitBackdropFilter = backdropFilterString;
      returnObj.backdropFilter = backdropFilterString;
    }
    return returnObj;
  };

  /**
   * Project an effects payload to flat CSS strings for a preview tile. Reuses
   * the same transform / filter / transition builders the canvas uses (so a
   * preview matches the real generated effect) and folds the outline fields
   * into a single shorthand. Self-perspective is included in `transform`.
   * Returns only the keys that have a value so callers can spread cleanly.
   * Looping / entry animations are out of scope (keyframes — driven by WAAPI).
   */
  public static buildEffectsPreviewStyle = (effects: IStylingEffects): IEffectsPreviewStyle => {
    const result: IEffectsPreviewStyle = {};
    if (effects.opacity !== undefined && effects.opacity !== null) {
      result.opacity = effects.opacity;
    }
    const transform: string | undefined = StyleHelper.buildTransformInlineStyle(
      effects.transforms,
      effects.perspective
    );
    if (transform) {
      result.transform = transform;
    }
    const transition: string | undefined = StyleHelper.buildTransitionInlineStyle(effects.transitions);
    if (transition) {
      result.transition = transition;
    }
    const filter: string | undefined = StyleHelper.buildFilterInlineStyle(effects.filters);
    if (filter) {
      result.filter = filter;
    }
    if (effects.outlineStyle && effects.outlineStyle !== "none") {
      const width: number = effects.outlineWidth ?? 1;
      const color: string = effects.outlineColor ?? "currentColor";
      result.outline = `${width}px ${effects.outlineStyle} ${color}`;
      if (effects.outlineOffset !== undefined && effects.outlineOffset !== null) {
        result.outlineOffset = `${effects.outlineOffset}px`;
      }
    }
    return result;
  };

  /**
   * Builds the CSS `transform:` declaration string for the canvas inline style.
   * Combines optional self-perspective (`perspective(...)` function) with the
   * per-item function chain (translate / scale / rotate / skew). Returns
   * `undefined` when there is nothing to emit so the caller can skip the
   * inline style key entirely. Mirror of the Backend EffectsCssBuilder
   * AppendTransformAndSelfPerspective logic — keep the two in sync when
   * editing either side.
   */
  private static buildTransformInlineStyle = (
    transforms: ITransformItem[] | undefined,
    selfPerspective: number | undefined
  ): string | undefined => {
    const fns: string[] = [];
    if (selfPerspective !== undefined && selfPerspective !== null && selfPerspective > 0) {
      fns.push(`perspective(${selfPerspective}px)`);
    }
    if (transforms && transforms.length > 0) {
      for (const item of transforms) {
        const fn = StyleHelper.buildTransformFunction(item);
        if (fn) fns.push(fn);
      }
    }
    if (fns.length === 0) return undefined;
    return fns.join(" ");
  };

  private static buildTransformFunction = (item: ITransformItem): string => {
    if (!item || !item.type) return "";
    switch (item.type) {
      case "move": {
        const x = item.x ?? 0;
        const y = item.y ?? 0;
        const z = item.z ?? 0;
        if (z !== 0) return `translate3d(${x}px,${y}px,${z}px)`;
        return `translate(${x}px,${y}px)`;
      }
      case "scale": {
        // Scale identity is 1, not 0 — Z != 1 dispatches to 3D.
        const x = item.x ?? 1;
        const y = item.y ?? 1;
        const z = item.z ?? 1;
        if (z !== 1) return `scale3d(${x},${y},${z})`;
        return `scale(${x},${y})`;
      }
      case "rotate": {
        const x = item.x ?? 0;
        const y = item.y ?? 0;
        const z = item.z ?? 0;
        // Pure Z → 2D rotate(); X/Y components present → axis chain.
        if (x === 0 && y === 0) return `rotate(${z}deg)`;
        const parts: string[] = [];
        if (x !== 0) parts.push(`rotateX(${x}deg)`);
        if (y !== 0) parts.push(`rotateY(${y}deg)`);
        if (z !== 0) parts.push(`rotateZ(${z}deg)`);
        return parts.join(" ");
      }
      case "skew": {
        // No Z axis in CSS — Z field on the item is ignored.
        const x = item.x ?? 0;
        const y = item.y ?? 0;
        return `skew(${x}deg,${y}deg)`;
      }
      default:
        return "";
    }
  };

  /**
   * Builds the CSS `transition:` declaration string. Empty entries (no
   * property) are skipped so the join never produces a stray comma.
   * cubic-bezier 4-tuple is emitted only when present + length === 4.
   */
  private static buildTransitionInlineStyle = (transitions: ITransitionItem[] | undefined): string | undefined => {
    if (!transitions || transitions.length === 0) return undefined;
    const parts: string[] = [];
    for (const t of transitions) {
      if (!t || !t.property) continue;
      let timing: string;
      if (t.timingFunction === "cubic-bezier" && t.cubicBezier && t.cubicBezier.length === 4) {
        timing = `cubic-bezier(${t.cubicBezier[0]},${t.cubicBezier[1]},${t.cubicBezier[2]},${t.cubicBezier[3]})`;
      } else {
        timing = t.timingFunction ?? "ease";
      }
      let entry = `${t.property} ${t.duration ?? 200}ms ${timing}`;
      if (t.delay !== undefined && t.delay !== null && t.delay > 0) {
        entry += ` ${t.delay}ms`;
      }
      parts.push(entry);
    }
    if (parts.length === 0) return undefined;
    return parts.join(", ");
  };

  /**
   * Builds the CSS `filter:` / `backdrop-filter:` declaration string for the
   * canvas inline style. Returns `undefined` when nothing to emit so the
   * caller can skip the inline style key entirely. Mirror of the Backend
   * EffectsCssBuilder filter logic — keep the two in sync when editing
   * either side. Array order is preserved (CSS spec: filter function order
   * matters).
   */
  private static buildFilterInlineStyle = (filters: IFilterItem[] | undefined): string | undefined => {
    if (!filters || filters.length === 0) return undefined;
    const parts: string[] = [];
    for (const f of filters) {
      const fn = StyleHelper.buildFilterFunction(f);
      if (fn) parts.push(fn);
    }
    if (parts.length === 0) return undefined;
    return parts.join(" ");
  };

  private static buildFilterFunction = (item: IFilterItem): string => {
    if (!item || !item.type) return "";
    switch (item.type) {
      case "blur":
        return `blur(${item.value ?? 0}px)`;
      case "brightness":
      case "contrast":
      case "saturate":
        return `${item.type}(${item.value ?? 1})`;
      case "grayscale":
      case "invert":
      case "sepia":
        return `${item.type}(${item.value ?? 100}%)`;
      case "hue-rotate":
        return `hue-rotate(${item.value ?? 0}deg)`;
      case "drop-shadow": {
        // Empty-string-aware fallback to stay byte-identical with the backend
        // EffectsCssBuilder (string.IsNullOrEmpty) — a blank color must not
        // emit `drop-shadow(... )` with a missing/trailing color token.
        const dropShadowColor: string = item.color && item.color.length > 0 ? item.color : "rgba(0,0,0,0.35)";
        return `drop-shadow(${item.offsetX ?? 0}px ${item.offsetY ?? 0}px ${item.blur ?? 0}px ${dropShadowColor})`;
      }
      default:
        return "";
    }
  };

  /* ----------------------------------------------------------------------- *
   * CSS-string → structured effects-item parsers (reverse of build*InlineStyle)
   *
   * The Effects panel stores transforms / transitions / filters as typed item
   * arrays, but the MCP/AI path (ui_render markup + ui_style.set) speaks raw
   * CSS strings. These parsers bridge that gap deterministically — the exact
   * inverse of buildTransformInlineStyle / buildTransitionInlineStyle /
   * buildFilterInlineStyle above — so `parse(build(items)) ≈ items` (minus the
   * `id`, which the designer apply layer mints with uuid). They are liberal in
   * input (accept the common CSS forms an LLM writes: `0.3s` or `300ms`,
   * `scale(1.05)` or `scale(1.05,1.05)`, `translateY(-2px)`, `120%` brightness)
   * and produce items structurally identical to defaultsForType / the panel
   * hydrators so a round-trip renders correctly in the panel. Looping/entry
   * `@keyframes` are intentionally NOT parsed from CSS strings (they can't ride
   * a `style=` attribute) — those go through the effect-preset channel.
   * ----------------------------------------------------------------------- */

  private static readonly TRANSITION_TIMING_KEYWORDS: Set<string> = new Set([
    "linear",
    "ease",
    "ease-in",
    "ease-out",
    "ease-in-out"
  ]);

  /** Split on a single-char separator, ignoring separators inside parentheses. */
  private static splitTopLevel = (input: string, separator: string): string[] => {
    const parts: string[] = [];
    let depth: number = 0;
    let current: string = "";
    for (const ch of input) {
      if (ch === "(") {
        depth++;
      } else if (ch === ")") {
        depth = Math.max(0, depth - 1);
      }
      if (ch === separator && depth === 0) {
        parts.push(current);
        current = "";
      } else {
        current += ch;
      }
    }
    parts.push(current);
    return parts;
  };

  /** Tokenize on whitespace, keeping `fn(...)` (incl. nested parens) intact. */
  private static tokenizeTopLevel = (input: string): string[] => {
    const tokens: string[] = [];
    let depth: number = 0;
    let current: string = "";
    for (const ch of input) {
      if (ch === "(") {
        depth++;
        current += ch;
        continue;
      }
      if (ch === ")") {
        depth = Math.max(0, depth - 1);
        current += ch;
        continue;
      }
      if (/\s/.test(ch) && depth === 0) {
        if (current.length > 0) {
          tokens.push(current);
          current = "";
        }
        continue;
      }
      current += ch;
    }
    if (current.length > 0) {
      tokens.push(current);
    }
    return tokens;
  };

  /** Extract the leading numeric value of a CSS token, stripping any unit. */
  private static parseCssNumber = (token: string): number => {
    const match: RegExpExecArray | null = /-?\d*\.?\d+/.exec(token.trim());
    return match ? parseFloat(match[0]) : 0;
  };

  /** `0.3s` / `300ms` → milliseconds; non-time tokens → undefined. */
  private static parseTimeToMs = (token: string): number | undefined => {
    const match: RegExpExecArray | null = /^(-?\d*\.?\d+)(ms|s)$/i.exec(token.trim());
    if (!match) {
      return undefined;
    }
    const value: number = parseFloat(match[1]);
    if (Number.isNaN(value)) {
      return undefined;
    }
    return match[2].toLowerCase() === "s" ? Math.round(value * 1000) : Math.round(value);
  };

  private static parseCubicBezier = (token: string): CubicBezierTuple | undefined => {
    const open: number = token.indexOf("(");
    const close: number = token.lastIndexOf(")");
    if (open < 0 || close <= open) {
      return undefined;
    }
    const nums: number[] = token
      .slice(open + 1, close)
      .split(",")
      .map((s: string): number => parseFloat(s.trim()));
    if (nums.length !== 4 || nums.some((n: number): boolean => Number.isNaN(n))) {
      return undefined;
    }
    return [nums[0], nums[1], nums[2], nums[3]];
  };

  /** Paren-aware extraction of every `name(args)` segment from a CSS value. */
  private static parseFunctionList = (css: string): ICssFunctionToken[] => {
    const tokens: ICssFunctionToken[] = [];
    const len: number = css.length;
    let i: number = 0;
    while (i < len) {
      while (i < len && !/[a-zA-Z-]/.test(css[i])) {
        i++;
      }
      let name: string = "";
      while (i < len && /[a-zA-Z0-9-]/.test(css[i])) {
        name += css[i];
        i++;
      }
      if (i >= len || css[i] !== "(") {
        if (name.length === 0) {
          i++;
        }
        continue;
      }
      i++; // skip "("
      let depth: number = 1;
      let args: string = "";
      while (i < len && depth > 0) {
        const ch: string = css[i];
        if (ch === "(") {
          depth++;
        } else if (ch === ")") {
          depth--;
          if (depth === 0) {
            break;
          }
        }
        args += ch;
        i++;
      }
      i++; // skip closing ")"
      if (name.length > 0) {
        tokens.push({ name: name.toLowerCase(), args: args.trim() });
      }
    }
    return tokens;
  };

  /**
   * Parse a CSS `transition:` value into ITransitionItem payloads (no `id`).
   * Multi-item shorthand is split on top-level commas. Within an entry the CSS
   * grammar is `property duration timing-function delay`; the first time token
   * is the duration, the second is the delay, and `cubic-bezier(...)` / named
   * easings resolve the timing function (`steps(...)` degrades to `linear`).
   */
  public static parseTransitionCss = (css: string | undefined): Omit<ITransitionItem, "id">[] => {
    if (!css || css.trim().length === 0) {
      return [];
    }
    const result: Omit<ITransitionItem, "id">[] = [];
    for (const entry of StyleHelper.splitTopLevel(css, ",")) {
      const tokens: string[] = StyleHelper.tokenizeTopLevel(entry.trim());
      if (tokens.length === 0) {
        continue;
      }
      let property: string | undefined;
      const times: number[] = [];
      let timingFunction: TransitionTimingFunction = "ease";
      let cubicBezier: CubicBezierTuple | undefined;
      for (const token of tokens) {
        const timeMs: number | undefined = StyleHelper.parseTimeToMs(token);
        if (timeMs !== undefined) {
          times.push(timeMs);
          continue;
        }
        const lower: string = token.toLowerCase();
        if (lower.startsWith("cubic-bezier(")) {
          const tuple: CubicBezierTuple | undefined = StyleHelper.parseCubicBezier(lower);
          if (tuple) {
            timingFunction = "cubic-bezier";
            cubicBezier = tuple;
          }
          continue;
        }
        if (lower.startsWith("steps(")) {
          timingFunction = "linear";
          continue;
        }
        if (StyleHelper.TRANSITION_TIMING_KEYWORDS.has(lower)) {
          timingFunction = lower as TransitionTimingFunction;
          continue;
        }
        if (property === undefined) {
          property = token;
        }
      }
      const item: Omit<ITransitionItem, "id"> = {
        property: property ?? "all",
        duration: times.length > 0 ? times[0] : 200,
        timingFunction
      };
      if (cubicBezier) {
        item.cubicBezier = cubicBezier;
      }
      if (times.length > 1 && times[1] > 0) {
        item.delay = times[1];
      }
      result.push(item);
    }
    return result;
  };

  /**
   * Parse a CSS `transform:` value into ITransformItem payloads (no `id`) plus
   * a lifted self-`perspective`. Unit conventions mirror defaultsForType
   * (move → px, scale → none + uniformScale, rotate/skew → deg) so the panel
   * shows the parsed items with the right axis controls.
   */
  public static parseTransformCss = (css: string | undefined): IParsedTransformCss => {
    const transforms: Omit<ITransformItem, "id">[] = [];
    let perspective: number | undefined;
    if (!css || css.trim().length === 0) {
      return { transforms };
    }
    for (const fn of StyleHelper.parseFunctionList(css)) {
      const nums: number[] = fn.args.split(",").map((s: string): number => StyleHelper.parseCssNumber(s));
      switch (fn.name) {
        case "perspective": {
          const value: number = StyleHelper.parseCssNumber(fn.args);
          if (value > 0) {
            perspective = value;
          }
          break;
        }
        case "translate":
          transforms.push({ type: "move", unit: "px", x: nums[0] ?? 0, y: nums[1] ?? 0, z: 0 });
          break;
        case "translatex":
          transforms.push({ type: "move", unit: "px", x: nums[0] ?? 0, y: 0, z: 0 });
          break;
        case "translatey":
          transforms.push({ type: "move", unit: "px", x: 0, y: nums[0] ?? 0, z: 0 });
          break;
        case "translatez":
          transforms.push({ type: "move", unit: "px", x: 0, y: 0, z: nums[0] ?? 0 });
          break;
        case "translate3d":
          transforms.push({ type: "move", unit: "px", x: nums[0] ?? 0, y: nums[1] ?? 0, z: nums[2] ?? 0 });
          break;
        case "scale": {
          // CSS carries no "linked axes" bit, so uniformScale is inferred from
          // x === y. A single-arg `scale(n)` is therefore uniform; the 3-arg
          // `scale3d` branch below always treats axes as independent.
          const x: number = nums[0] ?? 1;
          const y: number = nums.length > 1 ? (nums[1] ?? 1) : x;
          transforms.push({ type: "scale", unit: "none", x, y, z: 1, uniformScale: x === y });
          break;
        }
        case "scalex":
          transforms.push({ type: "scale", unit: "none", x: nums[0] ?? 1, y: 1, z: 1, uniformScale: false });
          break;
        case "scaley":
          transforms.push({ type: "scale", unit: "none", x: 1, y: nums[0] ?? 1, z: 1, uniformScale: false });
          break;
        case "scale3d":
          transforms.push({
            type: "scale",
            unit: "none",
            x: nums[0] ?? 1,
            y: nums[1] ?? 1,
            z: nums[2] ?? 1,
            uniformScale: false
          });
          break;
        case "rotate":
        case "rotatez":
          transforms.push({ type: "rotate", unit: "deg", x: 0, y: 0, z: nums[0] ?? 0 });
          break;
        case "rotatex":
          transforms.push({ type: "rotate", unit: "deg", x: nums[0] ?? 0, y: 0, z: 0 });
          break;
        case "rotatey":
          transforms.push({ type: "rotate", unit: "deg", x: 0, y: nums[0] ?? 0, z: 0 });
          break;
        case "skew":
          transforms.push({ type: "skew", unit: "deg", x: nums[0] ?? 0, y: nums[1] ?? 0 });
          break;
        case "skewx":
          transforms.push({ type: "skew", unit: "deg", x: nums[0] ?? 0, y: 0 });
          break;
        case "skewy":
          transforms.push({ type: "skew", unit: "deg", x: 0, y: nums[0] ?? 0 });
          break;
        default:
          break;
      }
    }
    const parsed: IParsedTransformCss = { transforms };
    if (perspective !== undefined) {
      parsed.perspective = perspective;
    }
    return parsed;
  };

  /**
   * Parse a CSS `filter:` / `backdrop-filter:` value into IFilterItem payloads
   * (no `id`). Percentage vs. multiplier forms are normalized to match the
   * panel's stored convention (brightness/contrast/saturate as multipliers;
   * grayscale/invert/sepia as 0–100). `drop-shadow(...)` keeps its flat
   * offset/blur/color fields.
   */
  public static parseFilterCss = (css: string | undefined): Omit<IFilterItem, "id">[] => {
    if (!css || css.trim().length === 0) {
      return [];
    }
    const result: Omit<IFilterItem, "id">[] = [];
    for (const fn of StyleHelper.parseFunctionList(css)) {
      switch (fn.name) {
        case "blur":
          result.push({ type: "blur", value: StyleHelper.parseCssNumber(fn.args), unit: "px" });
          break;
        case "brightness":
        case "contrast":
        case "saturate": {
          const raw: string = fn.args.trim();
          const value: number = raw.endsWith("%")
            ? StyleHelper.parseCssNumber(raw) / 100
            : StyleHelper.parseCssNumber(raw);
          result.push({ type: fn.name, value, unit: "none" });
          break;
        }
        case "grayscale":
        case "invert":
        case "sepia": {
          const raw: string = fn.args.trim();
          const value: number = raw.endsWith("%")
            ? StyleHelper.parseCssNumber(raw)
            : StyleHelper.parseCssNumber(raw) * 100;
          result.push({ type: fn.name, value, unit: "%" });
          break;
        }
        case "hue-rotate":
          result.push({ type: "hue-rotate", value: StyleHelper.parseCssNumber(fn.args), unit: "deg" });
          break;
        case "drop-shadow":
          result.push(StyleHelper.parseDropShadow(fn.args));
          break;
        default:
          break;
      }
    }
    return result;
  };

  private static parseDropShadow = (args: string): Omit<IFilterItem, "id"> => {
    const lengths: number[] = [];
    let color: string | undefined;
    for (const token of StyleHelper.tokenizeTopLevel(args)) {
      if (/^-?\d*\.?\d+(px|em|rem)?$/i.test(token)) {
        lengths.push(StyleHelper.parseCssNumber(token));
      } else {
        color = token;
      }
    }
    const item: Omit<IFilterItem, "id"> = {
      type: "drop-shadow",
      offsetX: lengths[0] ?? 0,
      offsetY: lengths[1] ?? 0,
      blur: lengths[2] ?? 0
    };
    if (color) {
      item.color = color;
    }
    return item;
  };

  static convertIStylingEffectsToIFormStylingEffects = (
    context: ICodeGenerationContext,
    kuikaid: string,
    kuikaStyle?: IStylingEffects
  ): IStylingEffects | undefined => {
    if (kuikaStyle && kuikaStyle.effectsId && kuikaStyle.effectsId.length > 0) {
      const effectsIdList = kuikaStyle.effectsId.split("|");
      if (effectsIdList !== undefined) {
        kuikaStyle.effectsIds = [];
        effectsIdList.forEach((effectsId) => {
          if (effectsId != null && effectsId.length > 0) {
            const effectsIdLower = effectsId?.toLowerCase();
            if (context.project.stylingEffects?.find((x) => x.effectsId?.toLowerCase() === effectsIdLower)) {
              kuikaStyle.effectsIds?.push(effectsIdLower);
            }
          }
        });
      }
      kuikaStyle.effectsId = null;
    }
    if (
      kuikaStyle &&
      context.projectTempStyles.tempStylingEffects &&
      kuikaid === context.projectTempStyles.tempStylingEffects.kuikaid
    ) {
      kuikaStyle.name = context.projectTempStyles.tempStylingEffects.name;
      kuikaStyle.isVisible = context.projectTempStyles.tempStylingEffects.isVisible;
      kuikaStyle.opacity = context.projectTempStyles.tempStylingEffects.opacity;
      kuikaStyle.cursor = context.projectTempStyles.tempStylingEffects.cursor;
      kuikaStyle.pointerEvents = context.projectTempStyles.tempStylingEffects.pointerEvents;
      kuikaStyle.mixBlendMode = context.projectTempStyles.tempStylingEffects.mixBlendMode;
      kuikaStyle.outlineStyle = context.projectTempStyles.tempStylingEffects.outlineStyle;
      kuikaStyle.outlineWidth = context.projectTempStyles.tempStylingEffects.outlineWidth;
      kuikaStyle.outlineColor = context.projectTempStyles.tempStylingEffects.outlineColor;
      kuikaStyle.outlineOffset = context.projectTempStyles.tempStylingEffects.outlineOffset;
      // Faz 3: Multi-item arrays + Transform Settings — temp style preview support.
      kuikaStyle.transforms = context.projectTempStyles.tempStylingEffects.transforms;
      kuikaStyle.transitions = context.projectTempStyles.tempStylingEffects.transitions;
      kuikaStyle.transformOriginX = context.projectTempStyles.tempStylingEffects.transformOriginX;
      kuikaStyle.transformOriginY = context.projectTempStyles.tempStylingEffects.transformOriginY;
      kuikaStyle.perspective = context.projectTempStyles.tempStylingEffects.perspective;
      kuikaStyle.childrenPerspective = context.projectTempStyles.tempStylingEffects.childrenPerspective;
      kuikaStyle.childrenPerspectiveOriginX = context.projectTempStyles.tempStylingEffects.childrenPerspectiveOriginX;
      kuikaStyle.childrenPerspectiveOriginY = context.projectTempStyles.tempStylingEffects.childrenPerspectiveOriginY;
      kuikaStyle.backfaceVisibility = context.projectTempStyles.tempStylingEffects.backfaceVisibility;
      kuikaStyle.transformStyle = context.projectTempStyles.tempStylingEffects.transformStyle;
      // Faz 4: Filters & Backdrop Filters — temp style preview support.
      kuikaStyle.filters = context.projectTempStyles.tempStylingEffects.filters;
      kuikaStyle.backdropFilters = context.projectTempStyles.tempStylingEffects.backdropFilters;
      // Reveal preset entry animation — keep the preview overlay in sync so a
      // re-render during editing doesn't show a stale (or missing) animation.
      kuikaStyle.entryAnimation = context.projectTempStyles.tempStylingEffects.entryAnimation;
      // Looping animations — same temp-style preview support as the other arrays.
      kuikaStyle.animations = context.projectTempStyles.tempStylingEffects.animations;
    }
    return kuikaStyle;
  };

  static convertIFormStylingEffectsToIStylingEffects = (
    kuikaid: string,
    kuikaStyleForm?: IStylingEffects,
    customStylingEffectsBackup?: IStylingEffects
  ): IStylingEffects | undefined => {
    if (kuikaStyleForm) {
      kuikaStyleForm.effectsId = null;
    }
    if (kuikaStyleForm && kuikaStyleForm.effectsIds && kuikaStyleForm.effectsIds.length > 0) {
      const validIds = kuikaStyleForm.effectsIds.filter((id) => id != null && id.length > 0);
      kuikaStyleForm.effectsId = validIds.length > 0 ? validIds.join("|") : null;
    }
    if (
      customStylingEffectsBackup &&
      kuikaid &&
      kuikaid.length > 0 &&
      kuikaStyleForm &&
      customStylingEffectsBackup?.kuikaid === kuikaid
    ) {
      // NOTE: this backup-restore overlay only carries Faz 1 scalars
      // (opacity, cursor, pointerEvents, mixBlendMode, outline*). Faz 3+4
      // multi-item arrays (transforms, transitions, filters, backdropFilters)
      // and Faz 3 Transform Settings scalars are INTENTIONALLY omitted —
      // those are owned by the per-item editors and must be edited there
      // directly. Adding them here would cause saved-preset overlays to clobber
      // user-entered multi-item edits when the preset is re-applied.
      kuikaStyleForm.name = customStylingEffectsBackup.name;
      kuikaStyleForm.isVisible = customStylingEffectsBackup.isVisible;
      kuikaStyleForm.opacity = customStylingEffectsBackup.opacity;
      kuikaStyleForm.cursor = customStylingEffectsBackup.cursor;
      kuikaStyleForm.pointerEvents = customStylingEffectsBackup.pointerEvents;
      kuikaStyleForm.mixBlendMode = customStylingEffectsBackup.mixBlendMode;
      kuikaStyleForm.outlineStyle = customStylingEffectsBackup.outlineStyle;
      kuikaStyleForm.outlineWidth = customStylingEffectsBackup.outlineWidth;
      kuikaStyleForm.outlineColor = customStylingEffectsBackup.outlineColor;
      kuikaStyleForm.outlineOffset = customStylingEffectsBackup.outlineOffset;
    }
    return kuikaStyleForm;
  };

  /* Fill */

  private static getFillStyles = (
    context: ICodeGenerationContext,
    styleObj: IKuikaStyle,
    generateType: SourceCodeGenerateTypes,
    kuikaid: string
  ): any => {
    const fillDef: IFillStyle | undefined = StyleHelper.convertIStylingFillToIFormStylingFill(
      context,
      kuikaid,
      styleObj?.fill
    );
    if (!fillDef) {
      return;
    }
    let returnObj = {} as any;
    if (fillDef.fillIds && fillDef.fillIds.length > 0) {
      for (const fillId of fillDef.fillIds) {
        const fillIdLower = fillId?.toLowerCase();
        const formStylingFill = context.project.stylingFill?.find((x) => x.fillId?.toLowerCase() === fillIdLower);
        if (formStylingFill) {
          returnObj = this.mergeStyle(returnObj, formStylingFill);
        }
      }
    }
    returnObj = this.mergeStyle(returnObj, fillDef);
    returnObj = StyleHelper.getFillStyle(returnObj, generateType);
    return returnObj;
  };

  private static getFillStyle = (styleObj: IFillStyle, generateType: SourceCodeGenerateTypes): any => {
    const returnObj = {} as any;
    if (!styleObj) {
      return returnObj;
    }

    const color = styleObj.color;
    if (color && color !== "") {
      if (isGradient(color)) {
        returnObj.backgroundImage = color;
      } else {
        returnObj.backgroundColor = color;
      }
    }

    const bgImage = styleObj.backgroundImage;
    if (bgImage && bgImage !== "") {
      if (Guid.isGuid(bgImage)) {
        returnObj.backgroundImage =
          generateType === SourceCodeGenerateTypes.generateClientCode
            ? `[imageResourceId:${bgImage}]`
            : `url(${CommonHelperFunctions.GetBackendUrl()}/resource/runtime/byid?resourceId=${bgImage})`;
      } else {
        returnObj.backgroundImage = `url(${bgImage})`;
      }
    }

    const repeat = styleObj.repeatSwitch;
    if (repeat === false) {
      returnObj.backgroundRepeat = "no-repeat";
    } else if (repeat === true) {
      returnObj.backgroundRepeat = "repeat";
    }

    const size = styleObj.bgImageSize;
    if (size === false) {
      returnObj.backgroundSize = "contain";
    } else if (size === true) {
      returnObj.backgroundSize = "cover";
    }

    if (styleObj.stretch) {
      returnObj.objectFit = "unset";
      returnObj.backgroundSize = "unset";
      returnObj.backgroundRepeat = "unset";
    }

    const position = styleObj.bgImagePosition;
    if (position === "center") {
      returnObj.backgroundPosition = "center";
    } else if (position === "top") {
      returnObj.backgroundPosition = "top";
    } else if (position === "right") {
      returnObj.backgroundPosition = "right";
    } else if (position === "bottom") {
      returnObj.backgroundPosition = "bottom";
    } else if (position === "left") {
      returnObj.backgroundPosition = "left";
    }

    return returnObj;
  };

  private static mergeStyle = (target: any, source: any) => {
    if (typeof target !== "object" || typeof source !== "object") {
      return source === null || source === undefined ? target : source;
    }

    const result = Array.isArray(target) ? [...target] : { ...target };
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        const value = source[key];
        result[key] = value === null || value === undefined ? target[key] : this.mergeStyle(target[key], value);
      }
    }
    return result;
  };

  /* Position */

  public static getPositionStyles = (context: ICodeGenerationContext, styleObj: IKuikaStyle, kuikaid: string): any => {
    if (!styleObj?.position?.width) delete styleObj.position?.widthStyle;
    if (!styleObj?.position?.height) delete styleObj.position?.heightStyle;
    if (!styleObj?.position?.minWidth) delete styleObj.position?.minWidthStyle;
    if (!styleObj?.position?.minHeight) delete styleObj.position?.minHeightStyle;
    if (!styleObj?.position?.maxWidth) delete styleObj.position?.maxWidthStyle;
    if (!styleObj?.position?.maxHeight) delete styleObj.position?.maxHeightStyle;

    const typoyDef: IStylingPosition | undefined = StyleHelper.convertIStylingPositionToIFormStylingPosition(
      context,
      kuikaid,
      styleObj?.position
    );
    if (!typoyDef) {
      return;
    }
    if (typoyDef.height === null) delete typoyDef.heightStyle;
    if (typoyDef.width === null) delete typoyDef.widthStyle;
    if (typoyDef.maxHeight === null) delete typoyDef.maxHeightStyle;
    if (typoyDef.maxWidth === null) delete typoyDef.maxWidthStyle;
    if (typoyDef.minHeight === null) delete typoyDef.minHeightStyle;
    if (typoyDef.minWidth === null) delete typoyDef.minWidthStyle;

    let returnObj = {} as any;
    if (typoyDef.positionIds && typoyDef.positionIds.length > 0) {
      for (const positionId of typoyDef.positionIds) {
        const positionIdLower = positionId?.toLowerCase();
        const formStylingPosition = context.project.stylingPosition.find(
          (x) => x.positionId?.toLowerCase() === positionIdLower
        );
        if (formStylingPosition) {
          returnObj = this.mergeStyle(returnObj, formStylingPosition);
        }
      }
    }
    returnObj = this.mergeStyle(returnObj, typoyDef);
    returnObj = StyleHelper.getPositionStyle(returnObj);
    return returnObj;
  };

  private static getPositionStyle = (formStylingPosition: IStylingPosition) => {
    const returnObj = {} as any;

    const isNode = typeof window === "undefined";
    const isFirefox = !isNode && navigator?.userAgent.toLowerCase().indexOf("firefox") > -1;

    // Direct assignment for margins and paddings
    if (formStylingPosition.marginTop !== undefined) returnObj.marginTop = formStylingPosition.marginTop;
    if (formStylingPosition.marginRight !== undefined) returnObj.marginRight = formStylingPosition.marginRight;
    if (formStylingPosition.marginBottom !== undefined) returnObj.marginBottom = formStylingPosition.marginBottom;
    if (formStylingPosition.marginLeft !== undefined) returnObj.marginLeft = formStylingPosition.marginLeft;

    if (formStylingPosition.paddingTop !== undefined) returnObj.paddingTop = formStylingPosition.paddingTop;
    if (formStylingPosition.paddingRight !== undefined) returnObj.paddingRight = formStylingPosition.paddingRight;
    if (formStylingPosition.paddingBottom !== undefined) returnObj.paddingBottom = formStylingPosition.paddingBottom;
    if (formStylingPosition.paddingLeft !== undefined) returnObj.paddingLeft = formStylingPosition.paddingLeft;

    if (formStylingPosition.width) {
      returnObj.width = formStylingPosition.width.toString() + (formStylingPosition.widthStyle || "px");
    }

    if (formStylingPosition.height) {
      returnObj.height = formStylingPosition.height.toString() + (formStylingPosition.heightStyle || "px");
    }

    if (formStylingPosition.maxWidth) {
      returnObj.maxWidth = formStylingPosition.maxWidth.toString() + (formStylingPosition.maxWidthStyle || "px");
    }

    if (formStylingPosition.maxHeight) {
      returnObj.maxHeight = formStylingPosition.maxHeight.toString() + (formStylingPosition.maxHeightStyle || "px");
    }

    if (formStylingPosition.minWidth) {
      returnObj.minWidth = formStylingPosition.minWidth.toString() + (formStylingPosition.minWidthStyle || "px");
    }

    if (formStylingPosition.minHeight) {
      returnObj.minHeight = formStylingPosition.minHeight.toString() + (formStylingPosition.minHeightStyle || "px");
    }

    if (formStylingPosition.alignItems) {
      if (formStylingPosition.alignItems === AlignItemsTypes.FlexStart) {
        returnObj.alignItems = "flex-start";
        // returnObj.display = "flex";
      } else if (formStylingPosition.alignItems === AlignItemsTypes.Center) {
        returnObj.alignItems = "center";
        // returnObj.display = "flex";
      } else if (formStylingPosition.alignItems === AlignItemsTypes.FlexEnd) {
        returnObj.alignItems = "flex-end";
        // returnObj.display = "flex";
      }
    }

    if (formStylingPosition.textAlign) {
      if (formStylingPosition.textAlign === TextAlignTypes.Left) {
        returnObj.textAlign = isFirefox ? "-moz-left" : "-webkit-left";
      } else if (formStylingPosition.textAlign === TextAlignTypes.Center) {
        returnObj.textAlign = isFirefox ? "-moz-center" : "-webkit-center";
      } else if (formStylingPosition.textAlign === TextAlignTypes.Right) {
        returnObj.textAlign = isFirefox ? "-moz-right" : "-webkit-right";
      }
    }

    if (formStylingPosition.display) {
      // if(returnObj.display !== "flex") {
      if (formStylingPosition.display === DisplayTypes.InlineBlock) {
        returnObj.display = "inline-block";
      } else if (formStylingPosition.display === DisplayTypes.InlineFlex) {
        returnObj.display = "inline-flex";
      } else if (formStylingPosition.display === DisplayTypes.Grid) {
        returnObj.display = "grid";
      } else if (formStylingPosition.display === DisplayTypes.Block) {
        returnObj.display = "block";
      } else if (formStylingPosition.display === DisplayTypes.Inline) {
        returnObj.display = "inline";
      } else if (formStylingPosition.display === DisplayTypes.None) {
        returnObj.display = "none";
      }
    }

    if (formStylingPosition.xAxis) {
      returnObj.xAxis = formStylingPosition.xAxis.toString();
    }

    if (formStylingPosition.yAxis) {
      returnObj.yAxis = formStylingPosition.yAxis.toString();
    }

    return returnObj;
  };

  static convertIStylingPositionToIFormStylingPosition = (
    context: ICodeGenerationContext,
    kuikaid: string,
    kuikaStyle?: IStylingPosition
  ): IStylingPosition | undefined => {
    if (kuikaStyle && kuikaStyle.positionId && kuikaStyle.positionId.length > 0) {
      const positionList = kuikaStyle.positionId.split("|");
      if (positionList !== undefined) {
        kuikaStyle.positionIds = [];
        positionList.forEach((positionId) => {
          if (positionId != null && positionId.length > 0) {
            const positionIdLower = positionId?.toLowerCase();
            if (context.project.stylingPosition?.find((x) => x.positionId?.toLowerCase() === positionIdLower)) {
              kuikaStyle.positionIds?.push(positionIdLower);
            }
          }
        });
      }
      kuikaStyle.positionId = null;
    }
    if (
      kuikaStyle &&
      context.projectTempStyles.tempStylingPosition &&
      kuikaid === context.projectTempStyles.tempStylingPosition.kuikaid
    ) {
      kuikaStyle.name = context.projectTempStyles.tempStylingPosition.name;
      // kuikaStyle.isMarginLocked = project.tempStylingPosition.isMarginLocked;
      // kuikaStyle.isPaddingLocked = project.tempStylingPosition.isPaddingLocked;
      kuikaStyle.marginTop = context.projectTempStyles.tempStylingPosition.marginTop;
      kuikaStyle.marginRight = context.projectTempStyles.tempStylingPosition.marginRight;
      kuikaStyle.marginBottom = context.projectTempStyles.tempStylingPosition.marginBottom;
      kuikaStyle.marginLeft = context.projectTempStyles.tempStylingPosition.marginLeft;
      kuikaStyle.paddingTop = context.projectTempStyles.tempStylingPosition.paddingTop;
      kuikaStyle.paddingRight = context.projectTempStyles.tempStylingPosition.paddingRight;
      kuikaStyle.paddingBottom = context.projectTempStyles.tempStylingPosition.paddingBottom;
      kuikaStyle.paddingLeft = context.projectTempStyles.tempStylingPosition.paddingLeft;
      kuikaStyle.width = context.projectTempStyles.tempStylingPosition.width;
      kuikaStyle.height = context.projectTempStyles.tempStylingPosition.height;
      kuikaStyle.widthStyle = context.projectTempStyles.tempStylingPosition.widthStyle;
      kuikaStyle.heightStyle = context.projectTempStyles.tempStylingPosition.heightStyle;
      kuikaStyle.maxWidth = context.projectTempStyles.tempStylingPosition.maxWidth;
      kuikaStyle.maxHeight = context.projectTempStyles.tempStylingPosition.maxHeight;
      kuikaStyle.maxWidthStyle = context.projectTempStyles.tempStylingPosition.maxWidthStyle;
      kuikaStyle.maxHeightStyle = context.projectTempStyles.tempStylingPosition.maxHeightStyle;
      kuikaStyle.minWidth = context.projectTempStyles.tempStylingPosition.minWidth;
      kuikaStyle.minHeight = context.projectTempStyles.tempStylingPosition.minHeight;
      kuikaStyle.minWidthStyle = context.projectTempStyles.tempStylingPosition.minWidthStyle;
      kuikaStyle.minHeightStyle = context.projectTempStyles.tempStylingPosition.minHeightStyle;
      kuikaStyle.positionId = context.projectTempStyles.tempStylingPosition.positionId;
      kuikaStyle.alignItems = context.projectTempStyles.tempStylingPosition.alignItems;
      kuikaStyle.textAlign = context.projectTempStyles.tempStylingPosition.textAlign;
      kuikaStyle.display = context.projectTempStyles.tempStylingPosition.display;
      kuikaStyle.xAxis = context.projectTempStyles.tempStylingPosition.xAxis;
      kuikaStyle.yAxis = context.projectTempStyles.tempStylingPosition.yAxis;
    }
    return kuikaStyle;
  };

  static convertIFormStylingPositionToIStylingPosition = (
    kuikaid: string,
    kuikaStyleForm?: IStylingPosition,
    customStylingPositionBackup?: IStylingPosition
  ): IStylingPosition | undefined => {
    if (kuikaStyleForm) {
      kuikaStyleForm.positionId = null;
    }
    if (kuikaStyleForm && kuikaStyleForm.positionIds && kuikaStyleForm.positionIds.length > 0) {
      // Use filter and join for efficient string building
      const validIds = kuikaStyleForm.positionIds.filter((id) => id != null && id.length > 0);
      kuikaStyleForm.positionId = validIds.length > 0 ? validIds.join("|") : null;
    }
    if (
      customStylingPositionBackup &&
      kuikaid &&
      kuikaid.length > 0 &&
      kuikaStyleForm &&
      customStylingPositionBackup?.kuikaid === kuikaid
    ) {
      kuikaStyleForm.name = customStylingPositionBackup.name;
      // kuikaStyleForm.isMarginLocked = customStylingPositionBackup.isMarginLocked;
      // kuikaStyleForm.isPaddingLocked = customStylingPositionBackup.isPaddingLocked;
      kuikaStyleForm.marginTop = customStylingPositionBackup.marginTop;
      kuikaStyleForm.marginRight = customStylingPositionBackup.marginRight;
      kuikaStyleForm.marginBottom = customStylingPositionBackup.marginBottom;
      kuikaStyleForm.marginLeft = customStylingPositionBackup.marginLeft;
      kuikaStyleForm.paddingTop = customStylingPositionBackup.paddingTop;
      kuikaStyleForm.paddingRight = customStylingPositionBackup.paddingRight;
      kuikaStyleForm.paddingBottom = customStylingPositionBackup.paddingBottom;
      kuikaStyleForm.paddingLeft = customStylingPositionBackup.paddingLeft;
      kuikaStyleForm.width = customStylingPositionBackup.width;
      kuikaStyleForm.height = customStylingPositionBackup.height;
      kuikaStyleForm.widthStyle = customStylingPositionBackup.widthStyle;
      kuikaStyleForm.heightStyle = customStylingPositionBackup.heightStyle;
      kuikaStyleForm.maxWidth = customStylingPositionBackup.maxWidth;
      kuikaStyleForm.maxHeight = customStylingPositionBackup.maxHeight;
      kuikaStyleForm.maxWidthStyle = customStylingPositionBackup.maxWidthStyle;
      kuikaStyleForm.maxHeightStyle = customStylingPositionBackup.maxHeightStyle;
      kuikaStyleForm.minWidth = customStylingPositionBackup.minWidth;
      kuikaStyleForm.minHeight = customStylingPositionBackup.minHeight;
      kuikaStyleForm.minWidthStyle = customStylingPositionBackup.minWidthStyle;
      kuikaStyleForm.minHeightStyle = customStylingPositionBackup.minHeightStyle;
      kuikaStyleForm.alignItems = customStylingPositionBackup.alignItems;
      kuikaStyleForm.textAlign = customStylingPositionBackup.textAlign;
      kuikaStyleForm.display = customStylingPositionBackup.display;
      kuikaStyleForm.xAxis = customStylingPositionBackup.xAxis;
      kuikaStyleForm.yAxis = customStylingPositionBackup.yAxis;
    }
    return kuikaStyleForm;
  };

  /* Text */
  private static getTextStyles = (context: ICodeGenerationContext, styleObj: IKuikaStyle, kuikaid: string): any => {
    const typoyDef: IStylingTypography | undefined = StyleHelper.convertIStylingTypographyToIFormStylingTypography(
      context,
      kuikaid,
      styleObj?.text
    );
    if (!typoyDef) {
      return;
    }
    let returnObj = {} as any;
    if (typoyDef.typoIds && typoyDef.typoIds.length > 0) {
      for (const typoId of typoyDef.typoIds) {
        const typoIdLower = typoId?.toLowerCase();
        const formStylingTypo = context.project.stylingTypography.find((x) => x.typoId?.toLowerCase() === typoIdLower);
        if (formStylingTypo) {
          returnObj = this.mergeStyle(returnObj, formStylingTypo);
        }
      }
    }

    returnObj = this.mergeStyle(returnObj, typoyDef);
    returnObj = StyleHelper.getTextStyle(returnObj);
    return returnObj;
  };

  static getTextStyle = (typoyDef: IStylingTypography): any => {
    const returnObj = {} as any;

    if (typoyDef.fontFamily) returnObj.fontFamily = typoyDef.fontFamily;
    if (typoyDef.fontWeight) returnObj.fontWeight = typoyDef.fontWeight;
    if (typoyDef.fontSize) {
      returnObj.fontSize = typoyDef.fontSize.toString() + (typoyDef.fontSizeStyle || "px");
    }

    if (typoyDef.color) {
      if (isGradient(typoyDef.color)) {
        // Gradient text: a gradient is invalid for `color`, so paint it as a
        // background clipped to the glyphs. A coexisting Fill is layered in later by
        // composeGradientTextOverFill (this builder only sees typography). NOTE: do
        // NOT set `color: transparent` — without background-clip:text support that
        // hides the text; unset `color` falls back to the inherited solid color.
        returnObj.backgroundImage = typoyDef.color;
        returnObj.WebkitBackgroundClip = "text";
        returnObj.backgroundClip = "text";
        returnObj.WebkitTextFillColor = "transparent";
      } else {
        returnObj.color = typoyDef.color;
      }
    }

    if (typoyDef.lineHeight) {
      returnObj.lineHeight = typoyDef.lineHeight.toString() + (typoyDef.lineHeightStyle || "px");
    }

    if (typoyDef.letterSpacing) {
      returnObj.letterSpacing = typoyDef.letterSpacing.toString() + (typoyDef.letterSpacingStyle || "px");
    }

    if (typoyDef.textDecoration) returnObj.textDecoration = typoyDef.textDecoration;
    if (typoyDef.fontStyle) returnObj.fontStyle = typoyDef.fontStyle;
    if (typoyDef.textTransform) returnObj.textTransform = typoyDef.textTransform as any;
    if (typoyDef.dontAffectedMobileSystem)
      returnObj.dontAffectedMobileSystem = typoyDef.dontAffectedMobileSystem as any;

    return returnObj;
  };

  static convertIStylingTypographyToIFormStylingTypography = (
    context: ICodeGenerationContext,
    kuikaid: string,
    kuikaStyle?: IStylingTypography
  ): IStylingTypography | undefined => {
    if (kuikaStyle && kuikaStyle.typoId && kuikaStyle.typoId.length > 0) {
      const typoIdList = kuikaStyle.typoId.split("|");
      if (typoIdList !== undefined) {
        kuikaStyle.typoIds = [];
        typoIdList.forEach((typoId) => {
          if (typoId != null && typoId.length > 0) {
            const typoIdLower = typoId?.toLowerCase();
            if (context.project.stylingTypography?.find((x) => x.typoId?.toLowerCase() === typoIdLower)) {
              kuikaStyle.typoIds?.push(typoIdLower);
            }
          }
        });
      }
      kuikaStyle.typoId = null;
    }
    if (
      kuikaStyle &&
      context.projectTempStyles.tempStylingTypography &&
      kuikaid === context.projectTempStyles.tempStylingTypography.kuikaid
    ) {
      kuikaStyle.name = context.projectTempStyles.tempStylingTypography.name;
      kuikaStyle.fontFamily = context.projectTempStyles.tempStylingTypography.fontFamily;
      kuikaStyle.fontWeight = context.projectTempStyles.tempStylingTypography.fontWeight;
      kuikaStyle.fontStyle = context.projectTempStyles.tempStylingTypography.fontStyle;
      kuikaStyle.fontSizeStyle = context.projectTempStyles.tempStylingTypography.fontSizeStyle;
      kuikaStyle.color = context.projectTempStyles.tempStylingTypography.color;
      kuikaStyle.lineHeight = context.projectTempStyles.tempStylingTypography.lineHeight;
      kuikaStyle.letterSpacing = context.projectTempStyles.tempStylingTypography.letterSpacing;
      kuikaStyle.lineHeightStyle = context.projectTempStyles.tempStylingTypography.lineHeightStyle;
      kuikaStyle.letterSpacingStyle = context.projectTempStyles.tempStylingTypography.letterSpacingStyle;
      kuikaStyle.textDecoration = context.projectTempStyles.tempStylingTypography.textDecoration;
      kuikaStyle.textTransform = context.projectTempStyles.tempStylingTypography.textTransform;
      kuikaStyle.dontAffectedMobileSystem = context.projectTempStyles.tempStylingTypography.dontAffectedMobileSystem;
    }
    return kuikaStyle;
  };

  static convertIFormStylingTypographyToIStylingTypography = (
    kuikaid: string,
    kuikaStyleForm?: IStylingTypography,
    customStylingTypographyBackup?: IStylingTypography
  ): IStylingTypography | undefined => {
    if (kuikaStyleForm) {
      kuikaStyleForm.typoId = null;
    }
    if (kuikaStyleForm && kuikaStyleForm.typoIds && kuikaStyleForm.typoIds.length > 0) {
      // Use filter, map and join for efficient string building
      const validIds = kuikaStyleForm.typoIds.filter((id) => id != null && id.length > 0);
      kuikaStyleForm.typoId = validIds.length > 0 ? validIds.map((id) => id.toLowerCase()).join("|") : null;
    }
    if (
      customStylingTypographyBackup &&
      kuikaid &&
      kuikaid.length > 0 &&
      kuikaStyleForm &&
      customStylingTypographyBackup?.kuikaid === kuikaid
    ) {
      kuikaStyleForm.name = customStylingTypographyBackup.name;
      kuikaStyleForm.fontFamily = customStylingTypographyBackup.fontFamily;
      kuikaStyleForm.fontWeight = customStylingTypographyBackup.fontWeight;
      kuikaStyleForm.fontStyle = customStylingTypographyBackup.fontStyle;
      kuikaStyleForm.fontSizeStyle = customStylingTypographyBackup.fontSizeStyle;
      kuikaStyleForm.fontSize = customStylingTypographyBackup.fontSize;
      kuikaStyleForm.color = customStylingTypographyBackup.color;
      kuikaStyleForm.lineHeight = customStylingTypographyBackup.lineHeight;
      kuikaStyleForm.letterSpacing = customStylingTypographyBackup.letterSpacing;
      kuikaStyleForm.lineHeightStyle = customStylingTypographyBackup.lineHeightStyle;
      kuikaStyleForm.letterSpacingStyle = customStylingTypographyBackup.letterSpacingStyle;
      kuikaStyleForm.textDecoration = customStylingTypographyBackup.textDecoration;
      kuikaStyleForm.textTransform = customStylingTypographyBackup.textTransform;
      kuikaStyleForm.dontAffectedMobileSystem = customStylingTypographyBackup.dontAffectedMobileSystem;
    }
    return kuikaStyleForm;
  };

  static convertIStylingFillToIFormStylingFill = (
    context: ICodeGenerationContext,
    kuikaid: string,
    kuikaStyle?: IFillStyle
  ): IFillStyle | undefined => {
    if (kuikaStyle && kuikaStyle.fillId && kuikaStyle.fillId.length > 0) {
      const fillIdList = kuikaStyle.fillId.split("|");
      if (fillIdList !== undefined) {
        kuikaStyle.fillIds = [];
        fillIdList.forEach((fillId) => {
          if (fillId != null && fillId.length > 0) {
            const fillIdLower = fillId?.toLowerCase();
            if (context.project.stylingFill?.find((x) => x.fillId?.toLowerCase() === fillIdLower)) {
              kuikaStyle.fillIds?.push(fillIdLower);
            }
          }
        });
      }
      kuikaStyle.fillId = null;
    }
    if (
      kuikaStyle &&
      context.projectTempStyles.tempStylingFill &&
      kuikaid === context.projectTempStyles.tempStylingFill.kuikaid
    ) {
      kuikaStyle.name = context.projectTempStyles.tempStylingFill.name;
      kuikaStyle.isVisible = context.projectTempStyles.tempStylingFill.isVisible;
      kuikaStyle.color = context.projectTempStyles.tempStylingFill.color;
      kuikaStyle.backgroundImage = context.projectTempStyles.tempStylingFill.backgroundImage;
      kuikaStyle.repeatSwitch = context.projectTempStyles.tempStylingFill.repeatSwitch;
      kuikaStyle.bgImageSize = context.projectTempStyles.tempStylingFill.bgImageSize;
      kuikaStyle.bgImagePosition = context.projectTempStyles.tempStylingFill.bgImagePosition;
    }
    return kuikaStyle;
  };

  static convertIFormStylingFillToIStylingFill = (
    kuikaid: string,
    kuikaStyleForm?: IFillStyle,
    customStylingFillBackup?: IFillStyle
  ): IFillStyle | undefined => {
    if (kuikaStyleForm) {
      kuikaStyleForm.fillId = null;
    }
    if (kuikaStyleForm && kuikaStyleForm.fillIds && kuikaStyleForm.fillIds.length > 0) {
      // Use filter, map and join for efficient string building
      const validIds = kuikaStyleForm.fillIds?.filter((id) => id != null && id.length > 0);
      kuikaStyleForm.fillId = validIds.length > 0 ? validIds.map((id) => id.toLowerCase()).join("|") : null;
    }
    if (
      customStylingFillBackup &&
      kuikaid &&
      kuikaid.length > 0 &&
      kuikaStyleForm &&
      customStylingFillBackup?.kuikaid === kuikaid
    ) {
      kuikaStyleForm.name = customStylingFillBackup.name;
      kuikaStyleForm.isVisible = customStylingFillBackup.isVisible;
      kuikaStyleForm.color = customStylingFillBackup.color;
      kuikaStyleForm.backgroundImage = customStylingFillBackup.backgroundImage;
      kuikaStyleForm.repeatSwitch = customStylingFillBackup.repeatSwitch;
      kuikaStyleForm.bgImageSize = customStylingFillBackup.bgImageSize;
      kuikaStyleForm.bgImagePosition = customStylingFillBackup.bgImagePosition;
    }
    return kuikaStyleForm;
  };

  /* Form part */

  static convertDiStyleToFormStyle = (
    context: ICodeGenerationContext,
    kuikaid: string,
    styleObj: IKuikaStyle
  ): IKuikaStyle => {
    const kuikaStyleForm: IKuikaStyle = {
      text: StyleHelper.convertIStylingTypographyToIFormStylingTypography(context, kuikaid, styleObj.text),
      border: StyleHelper.convertIStylingBorderToIFormStylingBorder(context, kuikaid, styleObj.border),
      fill: StyleHelper.convertIStylingFillToIFormStylingFill(context, kuikaid, styleObj.fill),
      position: StyleHelper.convertIStylingPositionToIFormStylingPosition(context, kuikaid, styleObj.position),
      shadow: StyleHelper.convertIStylingShadowToIFormStylingShadow(context, kuikaid, styleObj.shadow),
      effects: StyleHelper.convertIStylingEffectsToIFormStylingEffects(context, kuikaid, styleObj.effects),
      autoIncrementedIndex: styleObj.autoIncrementedIndex,
      // KOA-9069: the mirror of the hole in convertFormStyleToDiStyle. This literal is the ONLY
      // thing that survives a DI → form conversion, so a property missing here is erased by any
      // round-trip through the form — which deleted every conditional style on the component.
      // Conditions are not editable as form fields; they ride along untouched.
      conditionalStyles: styleObj.conditionalStyles
    };
    return kuikaStyleForm;
  };

  static convertFormStyleToDiStyle = (
    kuikaid: string,
    styleFormObj?: IKuikaStyle,
    customStylingPositionBackup?: IStylingPosition,
    customStylingTypographyBackup?: IStylingTypography,
    customStylingBorderBackup?: IStylingBorder,
    customStylingShadowBackup?: IStylingShadow,
    customStylingFillBackup?: IFillStyle,
    customStylingEffectsBackup?: IStylingEffects
  ): IKuikaStyle => {
    const kuikaStyle: IKuikaStyle = {
      text: StyleHelper.convertIFormStylingTypographyToIStylingTypography(
        kuikaid,
        styleFormObj?.text,
        customStylingTypographyBackup
      ),
      border: StyleHelper.convertIFormStylingBorderToIStylingBorder(
        kuikaid,
        styleFormObj?.border,
        customStylingBorderBackup
      ),
      fill: StyleHelper.convertIFormStylingFillToIStylingFill(kuikaid, styleFormObj?.fill, customStylingFillBackup),
      position: StyleHelper.convertIFormStylingPositionToIStylingPosition(
        kuikaid,
        styleFormObj?.position,
        customStylingPositionBackup
      ),
      shadow: StyleHelper.convertIFormStylingShadowToIStylingShadow(
        kuikaid,
        styleFormObj?.shadow,
        customStylingShadowBackup
      ),
      effects: StyleHelper.convertIFormStylingEffectsToIStylingEffects(
        kuikaid,
        styleFormObj?.effects,
        customStylingEffectsBackup
      ),
      autoIncrementedIndex: styleFormObj?.autoIncrementedIndex,
      // KOA-9069: carried through verbatim. This function rebuilds the DI style from an explicit
      // key list, so anything not named here is silently dropped on every style write — a
      // conditional style would have been discarded the moment any style was saved. It needs no
      // per-section conversion: each entry already holds the same `*StateStyles` shapes the state
      // buckets use, which the section converters above deliberately leave untouched.
      conditionalStyles: styleFormObj?.conditionalStyles
    };
    return kuikaStyle;
  };
}
